// v1.20 Belge şablonu yerleşim motoru: SAF fonksiyon (React/DOM yok). layoutDocument(template, data) → mm cinsinden sayfalar.
// Aynı model Faz 2'de sunucuda (reportlab) işlenecek: elementler {type,x,y,w,h,z,pages,style,...}, içerik yalnız beyaz listeli alanlardan düz metin.
// items_table satırları sabit satır yüksekliğiyle sayfalara akar; başlık her sayfada tekrar; pages: all|first|last.
import { formatIsoDay, formatMoney, formatNumber, formatPct, formatRate, formatTRY } from "./format";
import { amountInWords } from "./amountWords";

export const PAGE_MM = { A4: [210, 297], A5: [148, 210] };
const PAY = { nakit: "Nakit", havale: "Havale/EFT", kart: "Kredi Kartı", vadeli: "Vadeli", cek: "Çek", senet: "Senet", karma: "Karma" };
const PT = 0.3528;
const r2 = (v) => Math.round(v * 100) / 100;
export const rowHeight = (size) => size * PT * 1.55 + 1.2;

export const COLUMN_LABELS = { sira: "#", kod: "Kod", barkod: "Barkod", ad: "Ürün", miktar: "Miktar", birim: "Birim", birim_fiyat: "Birim Fiyat", iskonto: "İsk.",
  kdv_orani: "KDV %", kdv_tutari: "KDV Tutarı", tutar: "Tutar", koli: "Koli", not: "Ürün Açıklaması" };
export const TOTAL_LABELS = { subtotal: "Ara Toplam (Brüt)", discount: "İskonto", net: "KDV Matrahı", vat_lines: "Hesaplanan KDV (oran başına)", vat_total: "Toplam KDV",
  grand_total: "Genel Toplam", try_total: "TL Karşılığı", qty: "Toplam Miktar", koli: "Toplam Koli" };
export const PRICE_COLUMNS = ["birim_fiyat", "iskonto", "kdv_orani", "kdv_tutari", "tutar"];

export const vatBreakdown = (lines) => {
  const by = {};
  lines.forEach((l) => { const x = (by[l.vat_rate] ||= { rate: l.vat_rate, base: 0, vat: 0 }); x.base += l.line_net; x.vat += l.vat_amount; });
  return Object.values(by).sort((a, b) => a.rate - b.rate).map((x) => ({ ...x, base: r2(x.base), vat: r2(x.vat) }));
};

export function resolver({ o, meta, now, statusLabel, docTitle }) {
  const c = meta?.company || {}, cu = meta?.customer || {}, cur = o.currency;
  const pc = o.print_counts || {};
  const v = {
    "company.name": c.title, "company.address": c.address, "company.phone": c.phone, "company.email": c.email, "company.tax_office": c.tax_office, "company.tax_no": c.tax_no,
    "company.iban": c.iban, "company.brand": "Kaliteli Hizmet, Daima Memnuniyet",
    "customer.name": cu.title || o.customer_title, "customer.code": cu.code || o.customer_code, "customer.tax_no": cu.tax_no, "customer.tax_office": cu.tax_office,
    "customer.address": [cu.address, cu.district, cu.city].filter(Boolean).join(" · "), "customer.phone": cu.phone, "customer.city": cu.city,
    "order.no": o.order_no, "order.date": formatIsoDay(o.order_date), "order.due": formatIsoDay(o.due_date), "order.rep": o.sales_rep_name, "order.notes": o.note,
    "order.status": statusLabel || o.status, "order.currency": cur, "order.fx_rate": cur === "TRY" ? "TL" : `${cur} · Kur ${formatRate(o.fx_rate)}`,
    "invoice.no": o.invoice_no, "invoice.date": o.invoice_date ? formatIsoDay(o.invoice_date) : "",
    "totals.subtotal": formatMoney(o.subtotal, cur), "totals.discount": formatMoney(-o.discount_total, cur), "totals.net": formatMoney(o.net_total, cur),
    "totals.kdv": formatMoney(o.vat_total, cur), "totals.grand_total": formatMoney(o.grand_total, cur), "totals.grand_total_try": formatTRY(o.grand_total_try),
    "totals.amount_in_words": amountInWords(o.grand_total, cur), "totals.qty": formatNumber(o.lines.reduce((s, l) => s + l.qty, 0)),
    "totals.koli": formatNumber(o.lines.reduce((s, l) => s + (l.koli || 0), 0), 2), "payment.method": PAY[o.payment_method] || o.payment_method,
    "print.date": now, "print.count_summary": `Proforma ${pc.proforma || 0}x · Fatura ${pc.fatura || 0}x`, "doc.title": docTitle || "", "doc.no": o.invoice_no || o.order_no,
  };
  return (k) => (v[k] === undefined || v[k] === null ? "" : String(v[k]));
}

const fill = (text, get) => text.replace(/\{\{\s*([a-z_]+\.[a-z_]+)\s*\}\}/g, (_, k) => get(k));
const hasValue = (text, get) => { const ks = [...text.matchAll(/\{\{\s*([a-z_]+\.[a-z_]+)\s*\}\}/g)].map((m) => m[1]); return !ks.length || ks.some((k) => get(k)); };

function cell(key, l, i, cur) {
  switch (key) {
    case "sira": return String(i + 1);
    case "kod": return l.sku;
    case "barkod": return l.barcode || "";
    case "ad": return l.name;
    case "miktar": return formatNumber(l.qty);
    case "birim": return l.unit || "Adet";
    case "birim_fiyat": return formatMoney(l.unit_price, cur);
    case "iskonto": return formatPct(l.discount_pct);
    case "kdv_orani": return `%${l.vat_rate}`;
    case "kdv_tutari": return formatMoney(l.vat_amount, cur);
    case "tutar": return formatMoney(l.line_net, cur);
    case "koli": return formatNumber(l.koli, 2);
    case "not": return l.note || "";
    default: return "";
  }
}

function totals(rows, o) {
  const cur = o.currency, vat = vatBreakdown(o.lines), out = [];
  rows.forEach((k) => {
    if (k === "vat_lines") vat.forEach((v) => out.push([`Hesaplanan KDV %${v.rate}`, formatMoney(v.vat, cur), false]));
    else if (k === "try_total") { if (cur !== "TRY") out.push([`TL Karşılığı (Kur ${formatRate(o.fx_rate)})`, formatTRY(o.grand_total_try), false]); }
    else {
      const val = { subtotal: o.subtotal, discount: -o.discount_total, net: o.net_total, vat_total: o.vat_total, grand_total: o.grand_total }[k];
      if (k === "qty") out.push([TOTAL_LABELS.qty, formatNumber(o.lines.reduce((s, l) => s + l.qty, 0)), false]);
      else if (k === "koli") out.push([TOTAL_LABELS.koli, formatNumber(o.lines.reduce((s, l) => s + (l.koli || 0), 0), 2), false]);
      else out.push([k === "subtotal" ? "Ara Toplam (Brüt)" : TOTAL_LABELS[k], formatMoney(val, cur), k === "grand_total"]);
    }
  });
  return out;
}

function resolve(el, get, o, page, pageCount) {
  const t = el.type;
  if (t === "text") return el.hide_if_empty && !hasValue(el.text, get) ? null : { text: fill(el.text, get) };
  if (t === "field") { const v = get(el.field); return el.hide_if_empty && !v ? null : { text: el.label ? `${el.label} ${v}` : v }; }
  if (t === "page_number") return { text: `Sayfa ${page + 1} / ${pageCount}` };
  if (t === "totals_block") return { rows: totals(el.rows, o) };
  if (t === "vat_table") return { header: ["KDV Oranı", "Matrah", "KDV"], rows: vatBreakdown(o.lines).map((v) => [`%${v.rate}`, formatMoney(v.base, o.currency), formatMoney(v.vat, o.currency)]) };
  if (t === "kv_table") return { rows: el.kv.map((r) => [r.label, get(r.field)]) };
  if (t === "bank_info") return { text: ["IBAN: " + get("company.iban"), get("company.name")].filter((x) => x.trim() && x !== "IBAN: ").join("\n") };
  if (t === "image") return { src: el.src };
  return {};
}

export function layoutDocument(template, data) {
  const [w0, h0] = PAGE_MM[template.page?.size || "A4"];
  const [W, H] = template.page?.orientation === "landscape" ? [h0, w0] : [w0, h0];
  const m = template.page?.margins || { top: 12, bottom: 12 };
  const get = resolver(data);
  const o = data.o;
  const els = template.elements.filter((e) => !e.hidden).map((e) => (e.columns ? { ...e, columns: e.columns.filter((c) => !c.hidden) } : e)).sort((a, b) => (a.z || 0) - (b.z || 0));  // v1.27.0 gizli eleman/sütun yazdırılmaz
  const table = els.find((e) => e.type === "items_table");
  const chunks = [];
  if (table) {
    const rh = rowHeight(table.row_size || 8.5);
    const allRows = o.lines.map((l, i) => table.columns.map((c) => cell(c.key, l, i, o.currency)));
    const contTop = Math.max(m.top, ...els.filter((e) => e.pages === "all" && e !== table && e.y < table.y).map((e) => e.y + e.h + 2));
    // Son sayfa elemanları (toplamlar vb.) tablonun altında yer kaplar: son sayfada satırlar o alanın üstünde biter.
    const lastTop = Math.min(H - m.bottom, ...els.filter((e) => e.pages === "last" && e.y >= table.y).map((e) => e.y));
    const cap = (top, bottom) => Math.max(0, Math.floor((bottom - top - rh) / rh));
    const caps = [[Math.max(1, cap(table.y, table.y + table.h)), cap(table.y, Math.min(lastTop, table.y + table.h))], [Math.max(1, cap(contTop, H - m.bottom)), cap(contTop, lastTop)]];
    let rest = allRows;
    for (let first = true; ; first = false) {
      const [normal, last] = caps[first ? 0 : 1];
      if (rest.length <= last) { chunks.push(rest); break; }
      const take = Math.max(1, Math.min(normal, rest.length - 1));  // son sayfaya en az bir satır kalır
      chunks.push(rest.slice(0, take)); rest = rest.slice(take);
    }
    var tableInfo = { rh, contTop, lastTop };  // eslint-disable-line no-var
  } else chunks.push([]);
  const n = chunks.length;
  const pages = chunks.map((rows, p) => {
    const items = [];
    els.forEach((el) => {
      const first = p === 0, last = p === n - 1;
      if ((el.pages === "first" && !first) || (el.pages === "last" && !last)) return;
      if (el === table) {
        const y = first ? el.y : tableInfo.contTop;
        const h = first ? el.h : (p === n - 1 ? tableInfo.lastTop : H - m.bottom) - y;
        items.push({ ...el, y, h, header: el.columns.map((c) => c.label || COLUMN_LABELS[c.key]), rows, rowH: tableInfo.rh, startIndex: 0 });
        return;
      }
      if (!first && el.pages === "all" && table && el.y >= table.y && el.y < table.y + table.h) return;
      const c = resolve(el, get, o, p, n);
      if (c) items.push({ ...el, ...c });
    });
    return { items };
  });
  return { width: W, height: H, pages };
}
